import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Stack, Tooltip, Skeleton, CircularProgress } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PeopleIcon from "@mui/icons-material/People";
import TvIcon from "@mui/icons-material/Tv";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { alpha } from "@mui/material/styles";
import { tableFromIPC } from "apache-arrow";
import { geoPath, geoMercator } from "d3-geo";
import { fetchMetrics, getTopVI, loadGroupData } from "./utils/metricsClient";
import { getThemeConfig, THEME_COLORS } from "./utils/invariantMessAggregator";
import { getHHConfig } from "./utils/invarianth2hAggregator";
import { getIndustryConfig } from "./utils/invariantIndustryAggregator";
import { getPolConfig } from "./utils/polAggregators";
import { DistrictDemographicBlock, US_NATIONAL } from "./DistrictReportView";

/* -------------------------------------------------------------------------- */
/* CONSTANTS                                                                   */
/* -------------------------------------------------------------------------- */

const PRO_COLOR  = "#1A7A78";
const ANTI_COLOR = "#C0392B";
const NAVY       = "#1E3A5F";
const AMBER      = "#E8722A";

const THEME_KEY_TO_ARG_GROUP = {
  "theme_convincing__choice_pred_n":   "choice_arguments",
  "theme_convincing__everyday_pred_n": "everyday_arguments",
  "theme_convincing__safety_pred_n":   "safety_arguments",
  "theme_convincing__economy_pred_n":  "economic_arguments",
  "theme_convincing__environ_pred_n":  "environment_arguments",
};

const US_PARTY_COLOURS = { Republican:"#CC2936", Democrat:"#0057A8", Independent:"#6B7280", Other:"#6B7280" };
const normalizeUSParty = (raw = "") => {
  const s = String(raw).trim().toLowerCase();
  if (s.includes("republican") || s === "r" || s === "rep") return "Republican";
  if (s.includes("democrat")   || s === "d" || s === "dem") return "Democrat";
  if (s.includes("independent"))                             return "Independent";
  return "Other";
};
const usPartyColor = (name) => US_PARTY_COLOURS[normalizeUSParty(name)] || US_PARTY_COLOURS.Other;

const STATE_ABBR_TO_FIPS = {
  AL:"01",AK:"02",AZ:"04",AR:"05",CA:"06",CO:"08",CT:"09",DE:"10",DC:"11",FL:"12",
  GA:"13",HI:"15",ID:"16",IL:"17",IN:"18",IA:"19",KS:"20",KY:"21",LA:"22",ME:"23",
  MD:"24",MA:"25",MI:"26",MN:"27",MS:"28",MO:"29",MT:"30",NE:"31",NV:"32",NH:"33",
  NJ:"34",NM:"35",NY:"36",NC:"37",ND:"38",OH:"39",OK:"40",OR:"41",PA:"42",RI:"44",
  SC:"45",SD:"46",TN:"47",TX:"48",UT:"49",VT:"50",VA:"51",WA:"53",WV:"54",WI:"55",
  WY:"56",
};
const FIPS_TO_ABBR = Object.fromEntries(
  Object.entries(STATE_ABBR_TO_FIPS).map(([abbr, fips]) => [fips, abbr])
);

const rowsFromTable = (table) => table.toArray().map((row) => row.toJSON());
const fmt1  = (v) => Number.isFinite(v) ? `${v.toFixed(1)}%` : "—";
const fmtPP = (v) => { if (!Number.isFinite(v)) return null; return `${v >= 0 ? "+" : ""}${v.toFixed(1)} pp`; };
const normalizeDigits = (v) => String(v ?? "").replace(/\D/g, "");
const toDigitsNumber = (v) => {
  const n = Number(normalizeDigits(v));
  return Number.isFinite(n) ? n : null;
};

/* -------------------------------------------------------------------------- */
/* SMALL COMPONENTS                                                            */
/* -------------------------------------------------------------------------- */

function SectionLabel({ children, color = NAVY }) {
  return (
    <Typography sx={{
      fontSize:"0.70rem", fontWeight:800, textTransform:"uppercase",
      letterSpacing:"0.12em", color,
      pb:"2px", mb:"5px", display:"inline-block",
      borderBottom:`2px solid ${color}`,
    }}>
      {children}
    </Typography>
  );
}

function DeltaChip({ delta, neutral = 0.25, label = "state" }) {
  if (!Number.isFinite(delta)) return null;
  if (Math.abs(delta) < neutral) return (
    <Box sx={{ display:"inline-flex", bgcolor:"#f1f5f9", px:"4px", py:"1px", borderRadius:"4px", ml:"4px" }}>
      <Typography sx={{ fontSize:"0.63rem", color:"#94a3b8", fontWeight:700 }}>≈ {label}</Typography>
    </Box>
  );
  const up = delta > 0;
  return (
    <Box sx={{ display:"inline-flex", bgcolor: up ? "#dcfce7" : "#fee2e2", px:"4px", py:"1px", borderRadius:"4px", ml:"4px" }}>
      <Typography sx={{ fontSize:"0.63rem", fontWeight:800, color: up ? "#15803d" : "#b91c1c" }}>
        {fmtPP(delta)} vs {label}
      </Typography>
    </Box>
  );
}

function InlineBar({ value, maxValue = 100, color, label, height = 9, delta, subLabel }) {
  const pct = maxValue > 0 ? Math.min(100, Math.max(0, (value / maxValue) * 100)) : 0;
  return (
    <Box sx={{ mb:"6px" }}>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:"2px", gap:"4px" }}>
        <Box sx={{ flex:1, minWidth:0 }}>
          <Typography sx={{ fontSize:"0.68rem", color:"#475569", lineHeight:1.2 }}>{label}</Typography>
          {subLabel && <Typography sx={{ fontSize:"0.58rem", color:"#94a3b8", lineHeight:1.1, mt:"1px" }}>{subLabel}</Typography>}
        </Box>
        <Box sx={{ display:"flex", alignItems:"center", flexShrink:0 }}>
          <Typography sx={{ fontSize:"0.68rem", fontWeight:700, color:"#0f172a" }}>{fmt1(value)}</Typography>
          {Number.isFinite(delta) && <DeltaChip delta={delta} />}
        </Box>
      </Box>
      <Box sx={{ height, borderRadius:999, bgcolor:"#EEF2F7", overflow:"hidden" }}>
        <Box sx={{ height:"100%", width:`${pct}%`, bgcolor:color, borderRadius:999 }} />
      </Box>
    </Box>
  );
}

function SectionSkeleton({ height = 80, lines = 3 }) {
  return (
    <Box sx={{ p: "8px", borderRadius: "8px", border: "1px solid #e2e8f0", bgcolor: "white" }}>
      <Skeleton variant="text" width="40%" height={18} />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} variant="text" height={16} />
      ))}
      <Skeleton variant="rectangular" height={height} sx={{ mt: 1, borderRadius: "6px" }} />
    </Box>
  );
}
function HHPairRow({ proLabel, antiLabel, proVal, antiVal, shortLabel }) {
  const total  = (proVal + antiVal) || 100;
  const proPct = (proVal  / total) * 100;
  const aPct   = (antiVal / total) * 100;
  const margin = proVal - antiVal;
  const proWins = margin > 0;

  return (
    <Box sx={{ mb:"10px" }}>
      <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:"3px" }}>
        <Typography sx={{ fontSize:"0.64rem", fontWeight:700, color:"#64748b", letterSpacing:"0.06em", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", mr:"8px" }}>
          {shortLabel}
        </Typography>
        <Box sx={{
          display:"inline-flex", alignItems:"center", flexShrink:0,
          bgcolor: proWins ? alpha(PRO_COLOR,0.1) : alpha(ANTI_COLOR,0.1),
          px:"5px", py:"1px", borderRadius:"4px",
        }}>
          <Typography sx={{ fontSize:"0.6rem", fontWeight:800, color: proWins ? PRO_COLOR : ANTI_COLOR }}>
            {proWins ? "PRO" : "ANTI"} {fmtPP(Math.abs(margin))}
          </Typography>
        </Box>
      </Box>
      {/* Message labels above bar */}
      <Box sx={{ display:"flex", justifyContent:"space-between", mb:"2px" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:"3px", maxWidth:"50%" }}>
          <Box sx={{ width:6, height:6, borderRadius:"50%", bgcolor:PRO_COLOR, flexShrink:0 }} />
          <Typography sx={{ fontSize:"0.57rem", color:PRO_COLOR, fontWeight:700, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", lineHeight:1.2 }}>{proLabel}</Typography>
        </Box>
        <Box sx={{ display:"flex", alignItems:"center", gap:"3px", maxWidth:"50%", justifyContent:"flex-end" }}>
          <Typography sx={{ fontSize:"0.57rem", color:ANTI_COLOR, fontWeight:700, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textAlign:"right", lineHeight:1.2 }}>{antiLabel}</Typography>
          <Box sx={{ width:6, height:6, borderRadius:"50%", bgcolor:ANTI_COLOR, flexShrink:0 }} />
        </Box>
      </Box>
      {/* Bar with % values inside */}
      <Box sx={{ height:16, borderRadius:999, overflow:"hidden", display:"flex" }}>
        <Box sx={{ width:`${proPct}%`, bgcolor:PRO_COLOR, display:"flex", alignItems:"center", pl:"6px", overflow:"hidden" }}>
          <Typography sx={{ fontSize:"0.64rem", color:"white", fontWeight:800, whiteSpace:"nowrap" }}>{proVal.toFixed(1)}%</Typography>
        </Box>
        <Box sx={{ width:`${aPct}%`, bgcolor:ANTI_COLOR, display:"flex", alignItems:"center", justifyContent:"flex-end", pr:"6px", overflow:"hidden" }}>
          <Typography sx={{ fontSize:"0.64rem", color:"white", fontWeight:800, whiteSpace:"nowrap" }}>{antiVal.toFixed(1)}%</Typography>
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* DISTRICT COMMS PROFILE — hero synthesis card                               */
/* -------------------------------------------------------------------------- */

function DistrictCommsProfile({ messData, hhData, indData, industryData, topThemeColor, districtName, benchLabel, subjectLabel }) {
  if (!messData || !hhData) return null;

  const topTheme   = messData.topThemeName;
  const topArg     = messData.topMessagesAcrossThemes?.[0]?.label;
  const topHH      = hhData.topHH?.[0];
  const topMessenger = industryData?.topMessengers?.[0]?.label;

  // Persuadability label
  const legOpp     = indData?.legOpp ?? 0;
  const favChange  = industryData?.favMoved ?? 0; // % changed to more positive
  const hhMargin   = topHH?.margin ?? 0;
  const persuadabilityScore = (legOpp * 0.4) + (favChange * 0.4) + (Math.max(hhMargin, 0) * 0.2);
  let persuadabilityLabel, persuadabilityColor, persuadabilityDesc;
  if (persuadabilityScore >= 20) {
    persuadabilityLabel = "PERSUADE"; persuadabilityColor = "#15803d";
    persuadabilityDesc = "Strong opportunity — constituents are movable.";
  } else if (persuadabilityScore >= 10) {
    persuadabilityLabel = "HOLD"; persuadabilityColor = AMBER;
    persuadabilityDesc = "Mixed signals — reinforce, don't assume.";
  } else {
    persuadabilityLabel = "DEFEND"; persuadabilityColor = ANTI_COLOR;
    persuadabilityDesc = "Headwinds present — reactive framing needed.";
  }

  // Awareness level
  const awarenessAny = 100 - (industryData?.notHeardAwareness ?? 50);
  const awarenessLevel = awarenessAny >= 60 ? "High" : awarenessAny >= 35 ? "Moderate" : "Low";
  const awarenessNote = awarenessAny >= 60
    ? "Constituents are already engaged — go harder on problem framing."
    : awarenessAny >= 35
    ? "Mixed awareness — establish stakes before leading with solutions."
    : "Low awareness — build the case from the ground up.";

  // Build the narrative headline
  const deltaStr = messData.convValues?.[0]?.delta != null && Math.abs(messData.convValues[0].delta) >= 1
    ? `, ${messData.convValues[0].delta > 0 ? "over" : "under"}-indexing vs. ${benchLabel} by ${Math.abs(messData.convValues[0].delta).toFixed(1)} pp`
    : "";
  const narrative = [
    topTheme && `Lead with "${topTheme}"${deltaStr}.`,
    topArg   && `Best argument: "${topArg}".`,
    topHH    && hhMargin > 0 && `Strongest debate frame: "${topHH.proLabel}" (+${hhMargin.toFixed(1)} pp).`,
    topMessenger && `Put ${topMessenger} in front of ${subjectLabel}.`,
  ].filter(Boolean).join(" ");

  return (
    <Box sx={{
      mb:"6px", flexShrink:0,
      background:`linear-gradient(135deg, ${alpha(topThemeColor,0.08)} 0%, ${alpha(NAVY,0.04)} 100%)`,
      border:`1.5px solid ${alpha(topThemeColor,0.35)}`,
      borderLeft:`5px solid ${topThemeColor}`,
      borderRadius:"8px",
      p:"7px 10px",
    }}>
      <Box sx={{ display:"flex", alignItems:"flex-start", gap:"8px" }}>
        <CampaignIcon sx={{ fontSize:15, color:topThemeColor, flexShrink:0, mt:"1px" }} />
        <Box sx={{ flex:1, minWidth:0 }}>
          <Typography sx={{ fontSize:"0.56rem", fontWeight:900, color:topThemeColor, textTransform:"uppercase", letterSpacing:"0.13em", mb:"2px" }}>
            Recommended Approach
          </Typography>
          <Typography sx={{ fontSize:"0.74rem", color:NAVY, lineHeight:1.5, fontWeight:500 }}>
            {narrative || "Insufficient data to generate profile."}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* MAP — district highlighted in top theme color                             */
/* -------------------------------------------------------------------------- */

function DistrictThemeMap({ level = "cd", stateFips, district, themeName, topThemeColor, themeRows = [], themeKeys = [], themeColors = {}, onGeoReady }) {
  const [geo, setGeo] = useState(null);
  useEffect(() => {
    let ok = true;
    const url =
      level === "states"
        ? process.env.PUBLIC_URL + "/usmaps/us_state_simplified.json"
        :
      level === "statesenate"
        ? process.env.PUBLIC_URL + "/usmaps/us_statelegsenate_simplified.json"
        : level === "statehouse"
        ? process.env.PUBLIC_URL + "/usmaps/us_stateleghouse_simplified.json"
        : process.env.PUBLIC_URL + "/usmaps/us_cds_2024_simplified.json";
    fetch(url)
      .then(r => r.json()).then(j => { if (ok) setGeo(j); })
      .catch(() => {})
      // Signal the map's geo has resolved (success OR failure) so the render-
      // complete gate isn't left hanging on a map that never loads.
      .finally(() => { if (ok && typeof onGeoReady === "function") onGeoReady(); });
    return () => { ok = false; };
  }, [level]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!geo || !stateFips) return (
    <Box sx={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <Typography sx={{ fontSize:"0.6rem", color:"#94a3b8" }}>Loading…</Typography>
    </Box>
  );

  const fips  = String(stateFips).padStart(2,"0");
  const selId = level === "states"
    ? fips
    : level === "cd"
    ? `${fips}${String(district ?? 0).padStart(2,"0")}`
    : String(district ?? "");
  const stateField =
    geo.features?.length && "STATE" in (geo.features[0].properties || {})
      ? "STATE"
      : "STATEFP";
  const feats = level === "states"
    ? (geo.features || []).filter(f => String(f?.properties?.[stateField]) === fips)
    : (geo.features || []).filter(f => String(f?.properties?.[stateField]) === fips);
  const W = 280, H = 220;
  const proj = geoMercator(), path = geoPath(proj);

  // Try to find the selected district to zoom into it
  const selFeat = feats.find(f => {
    const gid = String(f?.properties?.GEOID || f?.properties?.geoid || "");
    const distStr = String(district ?? "");
    return gid === selId
      || gid === distStr
      || gid === distStr.padStart(3,"0")
      || gid === distStr.padStart(5,"0")
      || gid.endsWith(distStr.padStart(3,"0"))
      || gid.endsWith(distStr.padStart(2,"0"));
  });

  try {
    // Fit whole state first for correct geographic scale
    proj.fitExtent([[W*0.08,H*0.08],[W*0.92,H*0.92]], { type:"FeatureCollection", features:feats });
    if (selFeat) {
      // Measure district size at state scale
      const b = path.bounds(selFeat);
      const currentPx = Math.max(b[1][0] - b[0][0], b[1][1] - b[0][1]);
      // Zoom so district fills ~28% of viewport, clamped 1x-3.5x
      const zoom = currentPx > 0 ? Math.min(3.5, Math.max(1, (Math.min(W,H) * 0.28) / currentPx)) : 1;
      proj.scale(proj.scale() * zoom);
      // Reset translate to neutral, measure centroid fresh at new scale, then center it
      proj.translate([W/1.6, H/2]);
      const c = path.centroid(selFeat);
      proj.translate([W - c[0], H - c[1]]);
    }
  } catch {}

  const byGeoid = new Map();
  const normalizeDigits = (v) => String(v ?? "").replace(/\D/g,"");
  (themeRows || []).forEach(r => {
    const area = normalizeDigits(r?.area_id ?? "");
    let geoid = null;
    if (level === "states") {
      if (area) geoid = area.padStart(2,"0");
    } else if (level !== "cd") {
      if (area) geoid = area.padStart(5,"0");
      if (!geoid) {
        const st = normalizeDigits(r?.state_fips ?? r?.state ?? "");
        const di = normalizeDigits(r?.dist_fips ?? r?.district ?? "");
        if (st && di) geoid = `${st.padStart(2,"0")}${di.padStart(3,"0")}`;
      }
    } else if (area.length >= 4) {
      geoid = `${area.slice(0,2)}${area.slice(-2)}`;
    } else {
      const st = normalizeDigits(r?.state_fips ?? r?.state ?? "");
      const di = normalizeDigits(r?.dist_fips ?? "");
      if (st && di) geoid = `${st.padStart(2,"0")}${di.padStart(2,"0")}`;
    }
    if (!geoid) return;
    let bestKey = null; let bestVal = -Infinity;
    themeKeys.forEach(k => {
      const v = Number(r?.[k]);
      if (Number.isFinite(v) && v > bestVal) { bestVal = v; bestKey = k; }
    });
    if (bestKey) byGeoid.set(geoid, bestKey);
  });

  return (
    <Box sx={{ position:"relative", width:"100%", height:"100%" }}>
      <svg width={W} height={H} style={{ display:"block", width:"100%", height:"100%" }}>
        {feats.map((f,i) => {
          const isSel = f?.properties?.GEOID === selId;
          const key = byGeoid.get(String(f?.properties?.GEOID || ""));
          const base = key ? (themeColors?.[key] || "#94a3b8") : "#e2e8f0";
          return (
            <path
              key={i}
              d={path(f)||""}
              fill={isSel ? alpha(base,0.78) : alpha(base,0.38)}
              stroke={isSel ? base : alpha(base,0.55)}
              strokeWidth={isSel ? 2.4 : 0.8}
            />
          );
        })}
      </svg>
      {themeName && (
        <Box sx={{ position:"absolute", bottom:4, left:4, right:4, display:"flex", alignItems:"center", gap:"4px", bgcolor:"rgba(255,255,255,0.9)", px:"6px", py:"3px", borderRadius:"4px", border:`1px solid ${alpha(topThemeColor || NAVY,0.3)}` }}>
          <Box sx={{ width:8, height:8, borderRadius:"50%", bgcolor:topThemeColor || NAVY, flexShrink:0 }} />
          <Typography sx={{ fontSize:"0.52rem", fontWeight:700, color:topThemeColor || NAVY, lineHeight:1.2 }}>Top theme: {themeName}</Typography>
        </Box>
      )}
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* HEADER                                                                      */
/* -------------------------------------------------------------------------- */

function ClientTopHeader({ districtName, state, district, topVI, viRow, currentParty, stackLogo, pcpcLogo, invariantLogo }) {
  const projParty  = viRow?.winner ? normalizeUSParty(viRow.winner) : topVI?.label ?? null;
  const normWinner = viRow?.winner ? normalizeUSParty(viRow.winner) : null;
  const demN = viRow?.dem_n != null ? Number(viRow.dem_n) : null;
  const repN = viRow?.rep_n != null ? Number(viRow.rep_n) : null;
  const tot  = (demN ?? 0) + (repN ?? 0);
  const wShare = tot > 0 ? normWinner==="Democrat" && demN!=null ? demN/tot : normWinner==="Republican" && repN!=null ? repN/tot : null : null;
  const projShare = wShare != null ? wShare*100 : Number.isFinite(+topVI?.district) ? +topVI.district : undefined;
  const accent = projParty ? usPartyColor(projParty) : PRO_COLOR;

  return (
    <Box sx={{ mb:"5px" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb:"4px" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box component="img" src={stackLogo} alt="Stack" sx={{ height:15, opacity:0.85 }} />
          <Box component="img" src={pcpcLogo} alt="PCPC" sx={{ height:13, opacity:0.85 }} />
          <Box component="img" src={invariantLogo} alt="Invariant" sx={{ height:15, opacity:0.9 }} />
        </Stack>
        <Typography sx={{ fontSize:"0.58rem", color:"#94a3b8" }}>Prepared by Stack Data Strategy</Typography>
      </Stack>
      <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:"10px", mb:"3px" }}>
        <Typography sx={{ fontWeight:800, fontSize:"1.45rem", letterSpacing:-0.5, lineHeight:1.05, color:NAVY }}>
          {districtName || `${state}-${district}`}
        </Typography>
        <Box sx={{ display:"flex", gap:"6px", flexShrink:0 }}>
          {projParty && (
            <Box sx={{ display:"inline-flex", alignItems:"center", gap:"4px", px:"8px", py:"2px", borderRadius:999, bgcolor:alpha(accent,0.08), border:`1px solid ${alpha(accent,0.22)}` }}>
              <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:accent }} />
              <Typography sx={{ fontSize:"0.58rem", fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.5px" }}>Projection</Typography>
              <Typography sx={{ fontSize:"0.68rem", fontWeight:800, color:"#0f172a" }}>{projParty}{Number.isFinite(projShare) ? ` ${projShare.toFixed(1)}%` : ""}</Typography>
            </Box>
          )}
          {currentParty && (
            <Box sx={{ display:"inline-flex", alignItems:"center", gap:"4px", px:"8px", py:"2px", borderRadius:999, bgcolor:alpha(usPartyColor(currentParty),0.07), border:`1px solid ${alpha(usPartyColor(currentParty),0.2)}` }}>
              <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:usPartyColor(currentParty) }} />
              <Typography sx={{ fontSize:"0.58rem", fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.5px" }}>Current Rep.</Typography>
              <Typography sx={{ fontSize:"0.68rem", fontWeight:800, color:"#0f172a" }}>{normalizeUSParty(currentParty)}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ height:3, width:"clamp(50px,20%,120px)", borderRadius:4, background:`linear-gradient(90deg,${accent},${alpha(accent,0.3)})` }} />
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* MAIN COMPONENT                                                              */
/* -------------------------------------------------------------------------- */

const InvariantDistrictReportView = ({ id, districtName, state, district, stateFips, stateName, areaCode, areaType = "cd", clientLabel, onReady }) => {
  const stackLogo    = process.env.PUBLIC_URL + "/static/images/stack_logo.png";
  const pcpcLogo     = process.env.PUBLIC_URL + "/static/images/pcpc.png";
  const invariantLogo= process.env.PUBLIC_URL + "/static/images/invariant.svg";

  const isCD = areaType === "cd";
  const isStateReport = areaType === "states";
  const benchLabel = isStateReport ? "national" : "state";
  const benchLabelLong = isStateReport ? "the nation" : "the state";
  const benchWideLabel = isStateReport ? "nationwide" : "statewide";
  const subjectLabel = isStateReport ? "this state" : "this district";
  const areaLabel =
    areaType === "states" ? "State"
    : areaType === "statesenate" ? "State Senate"
    : areaType === "statehouse" ? "State House"
    : "CD";
  const dataLevel = isCD ? "cd" : areaType;
  const metricsAreaCode = isCD ? areaCode : areaCode;

  /* ── VI / partisan lean ── */
  const [charts, setCharts] = useState({ vi:[], loading:true });
  const topVI   = getTopVI(charts.vi);
  const prevArea = useRef(null);
  useEffect(() => {
    if (!metricsAreaCode || metricsAreaCode === prevArea.current) return;
    prevArea.current = metricsAreaCode;
    setCharts({ vi:[], loading:true });
    (isCD ? loadGroupData({ groupId:"vote_intention", areaCode: metricsAreaCode }) : Promise.resolve([]))
      .then(vi  => setCharts({ vi, loading:false }))
      .catch(() => setCharts({ vi:[], loading:false }));
  }, [metricsAreaCode, isCD]);

  const [viRow, setViRow] = useState(null);
  const stateAbbr = String(state || "").toUpperCase();
  useEffect(() => {
    if (!isCD) {
      setViRow(null);
      return;
    }
    let isActive = true;
    const load = async () => {
      try {
        const res = await fetch("/api/cd_vi_preds/");
        if (!res.ok) throw new Error();
        const ct = res.headers.get("content-type") || "";
        let rows;
        if (ct.includes("application/json") || ct.includes("text")) {
          rows = JSON.parse((await res.text()).replace(/:\s*NaN/g,": null"));
        } else {
          rows = tableFromIPC(await res.arrayBuffer()).toArray().map(r => r.toJSON());
        }
        if (!isActive) return;
        const dKey = String(district || "").padStart(2,"0");
        const isAtLarge = dKey === "00" || (stateAbbr === "AK" && dKey === "01");
        const keys = [`${stateAbbr}-${dKey}`,`${stateAbbr}-${Number(dKey)}`,isAtLarge?`${stateAbbr}-AL`:null,`${stateAbbr}-00`]
          .filter(Boolean).map(k => k.toUpperCase());
        setViRow((rows||[]).find(r => keys.includes(String(r?.state_CD119||"").toUpperCase())) || null);
      } catch { if (isActive) setViRow(null); }
    };
    if (stateAbbr) load();
    return () => { isActive = false; };
  }, [stateAbbr, district, isCD]);

  /* ── Demographic data (from KeyInfo source, same format) ── */
  const [demRow, setDemRow]         = useState(null);
  const [demoBench, setDemoBench]   = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  useEffect(() => {
    if (!stateFips || (isCD && district == null) || (!isCD && !areaCode)) return;
    let isActive = true;
    const fetchDemo = async () => {
      try {
        setDemoLoading(true);
        const resolvedFips = String(stateFips || STATE_ABBR_TO_FIPS[String(state).toUpperCase()] || "").padStart(2, "0");
        const level = isCD ? "cd" : areaType;
        const areaId = isCD
          ? `${resolvedFips}${String(district ?? "").padStart(2, "0")}`
          : String(areaCode ?? "");
        const [lvlRes, stateRes] = await Promise.all([
          fetch(`/api/${level}_political_data_arrow/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(isCD ? {} : { stateCode: resolvedFips }),
          }),
          fetch("/api/states_political_data_arrow/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ stateCode: resolvedFips }),
          }),
        ]);
        if (!lvlRes.ok || !stateRes.ok) throw new Error("demo fetch failed");

        const lvlRows   = rowsFromTable(tableFromIPC(await lvlRes.arrayBuffer()));
        const stateRows = rowsFromTable(tableFromIPC(await stateRes.arrayBuffer()));

        const match = lvlRows.find((r) => {
          const rArea = r?.area_id != null ? String(r.area_id) : "";
          if (rArea && rArea === String(areaId)) return true;
          const rDigits = normalizeDigits(rArea);
          if (rDigits && normalizeDigits(areaId) === rDigits) return true;
          if (!isCD) return false;
          const rState = normalizeDigits(r?.state_fips ?? r?.state ?? "");
          const rDist = normalizeDigits(r?.dist_fips ?? r?.district ?? "");
          const distStr = String(district ?? "");
          const distCandidates = new Set([
            distStr,
            distStr.padStart(2, "0"),
            distStr.padStart(3, "0"),
            distStr.padStart(4, "0"),
            distStr.padStart(5, "0"),
          ]);
          return rState === resolvedFips && rDist && distCandidates.has(rDist);
        });

        const stMatch = stateRows?.find((r) => String(r?.area_id ?? "") === String(resolvedFips)) || stateRows?.[0] || null;

        if (!isActive) return;
        setDemRow(match || null);
        setDemoBench(stMatch || null);
      } catch {
        if (!isActive) return;
        setDemRow(null);
        setDemoBench(null);
      } finally {
        if (isActive) setDemoLoading(false);
      }
    };
    fetchDemo();
    return () => { isActive = false; };
  }, [state, district, stateFips, areaType, areaCode, isCD]);

  /* ── Invariant survey data ── */
  const [messData,      setMessData]      = useState(null);
  const [hhData,        setHhData]        = useState(null);
  const [indData,       setIndData]       = useState(null);
  const [industryData,  setIndustryData]  = useState(null);
  const [topIssuesData, setTopIssuesData] = useState([]);
  const [themeRows,     setThemeRows]     = useState([]);
  const [surveyLoading, setSurveyLoading] = useState(true);

  useEffect(() => {
    if (!areaCode) return;
    let isActive = true;
    setSurveyLoading(true);

    const { groupsById: messGroups } = getThemeConfig();
    const { groupsById: hhGroups   } = getHHConfig();
    const { groupsById: indGroups  } = getIndustryConfig();
    const { groupsById: polGroups  } = getPolConfig();

    const messKeys     = Object.values(messGroups).flatMap(g => g.keys);
    const hhKeys       = Object.values(hhGroups).flatMap(g => g.keys);
    const topIssueKeys = polGroups.pol_top_issues?.keys ?? [];

    // Expanded industry keys — now includes information, awareness, news_source,
    // buy_approach, ind_favorability, leg_support, and industry_posneg_change
    const indKeys = [
      ...indGroups.industry_posneg.keys,
      ...indGroups.leg_support.keys,
      ...indGroups.information.keys,
      ...indGroups.awareness.keys,
      ...indGroups.news_source.keys,
      ...indGroups.buy_approach.keys,
      ...(indGroups.industry_posneg_change ? indGroups.industry_posneg_change.keys : []),
    ];
    // Also include the favorability-change keys from the mess aggregator
    const favChangeKeys = messGroups.industry_posneg_change?.keys ?? [];

    const resolvedFips = stateFips || STATE_ABBR_TO_FIPS[stateAbbr];

    const normAreaCode = String(areaCode || "").trim().toUpperCase();
    const normalizeAreaCode = (v) => String(v ?? "").trim().toUpperCase();
    const normalizeDigits = (v) => String(v ?? "").replace(/\D/g, "");
    const targetAreaIds = (() => {
      if (!isCD) {
        const norm = normalizeDigits(areaCode ?? "");
        const direct = normalizeAreaCode(areaCode ?? "");
        return new Set([norm, direct].filter(Boolean));
      }
      const distStr = String(district ?? "");
      const distCandidates = [distStr, distStr.padStart(2,"0"), distStr.padStart(3,"0"), distStr.padStart(4,"0"), distStr.padStart(5,"0")];
      const fips = String(resolvedFips || "").padStart(2, "0");
      return new Set(distCandidates.map((d) => `${fips}${d}`));
    })();

    const rowMatchesArea = (row) => {
      if (!row) return false;
      const direct = [row.area_id, row.area_code, row.area, row.cd, row.state_CD119, row.state_cd119]
        .map(normalizeAreaCode).filter(Boolean);
      if (direct.includes(normAreaCode)) return true;
      const st = normalizeAreaCode(row.state_abbr ?? row.state ?? row.state_code);
      const dist = normalizeDigits(row.dist ?? row.district ?? row.cd_dist ?? row.dist_fips ?? row.cd);
      if (st && dist) {
        const key = `${st}-${dist.padStart(2, "0")}`;
        if (key === normAreaCode) return true;
      }
      const areaDigits = normalizeDigits(row.area_id ?? "");
      if (areaDigits && targetAreaIds.has(areaDigits)) return true;
      if (!isCD) {
        const direct = normalizeAreaCode(row.area_id ?? row.geoid ?? row.GEOID ?? "");
        if (direct && targetAreaIds.has(direct)) return true;
      }
      if (areaDigits.length >= 4) {
        const fips = areaDigits.slice(0, 2);
        const d2 = areaDigits.slice(-2);
        const abbr = FIPS_TO_ABBR[fips];
        if (abbr && `${abbr}-${d2}` === normAreaCode) return true;
      }
      return false;
    };

    const findRow   = (rows) => rows?.find(rowMatchesArea) || rows?.[0] || null;
    const findStRow = (rows) =>
      rows?.find((r) => {
        const area = normalizeDigits(r?.area_id ?? "");
        const st   = normalizeDigits(r?.state_fips ?? r?.state ?? "");
        return String(area || st) === String(resolvedFips || "");
      }) || rows?.[0] || null;

    // District row matcher for pol/top-issues — handles CD, stateleg senate, stateleg house
    const findTopIssueRow = (rows) => {
      if (!rows?.length) return null;
      if (!isCD) {
        // Stateleg: match purely on numeric digits of area_id vs areaCode
        const targetDigits = toDigitsNumber(areaCode ?? "");
        return (
          rows.find((r) => {
            const areaDigits = toDigitsNumber(r?.area_id ?? r?.geoid ?? r?.GEOID ?? r?.district ?? "");
            return areaDigits != null && targetDigits != null && areaDigits === targetDigits;
          }) || null  // no fallback — null means no match, don't show wrong data
        );
      }
      // CD: match on stateFips + district number
      const f = String(resolvedFips || "").padStart(2, "0");
      const distRaw = normalizeDigits(district ?? "");
      const distVariants = new Set(
        [distRaw, distRaw.padStart(2,"0"), distRaw.padStart(3,"0"),
         distRaw.padStart(4,"0"), distRaw.padStart(5,"0")].filter(Boolean)
      );
      const areaCandidates = new Set(Array.from(distVariants).map(d => `${f}${d}`));
      return (
        rows.find((r) => {
          const rArea  = normalizeDigits(r?.area_id ?? r?.geoid ?? r?.GEOID ?? "");
          if (rArea && areaCandidates.has(rArea)) return true;
          const rState = String(r?.state_fips ?? r?.state ?? "").replace(/\D/g,"").padStart(2,"0");
          const rDist  = normalizeDigits(r?.dist_fips ?? r?.district ?? "");
          return rState === f && rDist && distVariants.has(rDist);
        }) || null
      );
    };

    // State benchmark matcher for pol/top-issues tracking rows
    const findTopIssueStateRow = (rows) => {
      if (!rows?.length) return null;
      const f    = String(resolvedFips || "").padStart(2, "0");
      const abbr = FIPS_TO_ABBR[f] ?? stateAbbr;
      return (
        rows.find((r) => {
          const rFips = String(r?.state_fips ?? r?.area_id ?? "").replace(/\D/g,"").padStart(2,"0");
          if (f && rFips === f) return true;
          const rAbbr = String(r?.state ?? r?.state_abbr ?? r?.state_code ?? "").trim().toUpperCase();
          return abbr && rAbbr === abbr;
        }) || null
      );
    };

    const allIndKeys = [...new Set([...indKeys, ...favChangeKeys])];

    const computeWeightedAvgRow = (rows, keys) => {
      if (!rows?.length) return null;
      const weights = rows.map((r) => Number(r?.total_sum)).map((w) => (Number.isFinite(w) ? w : 0));
      const totalW = weights.reduce((a, b) => a + b, 0);
      const avg = {};
      keys.forEach((k) => {
        let sum = 0;
        let count = 0;
        rows.forEach((r, i) => {
          const v = Number(r?.[k]);
          if (!Number.isFinite(v)) return;
          if (totalW > 0) {
            sum += v * (weights[i] || 0);
          } else {
            sum += v;
            count += 1;
          }
        });
        avg[k] = totalW > 0 ? sum / totalW : (count > 0 ? sum / count : null);
      });
      return avg;
    };

    const fetchTopIssueRows = async (level, stateCodes = [], wave = 3) => {
      const allStateFips = Object.values(STATE_ABBR_TO_FIPS);
      const resolvedStateCodes = level === "states" && (!stateCodes || stateCodes.length === 0)
        ? allStateFips
        : stateCodes;
      const res = await fetch(`/api/${level}_data_arrow/`, {
        method: "POST",
        headers: { "Content-Type":"application/json" },
        body: JSON.stringify({
          theme: "pol",
          groupKeys: topIssueKeys,
          stateCodes: resolvedStateCodes,
          schema: "tracking",
          schema_type: "tracking",
          wave,
        }),
      });
      if (!res.ok) throw new Error(`top issues ${level} ${res.status}`);
      const buf = await res.arrayBuffer();
      return rowsFromTable(tableFromIPC(buf)) || [];
    };

    Promise.all([
      // district
      fetchMetrics({ level:dataLevel, areaCodes:[areaCode],      theme:"invariant_message",  groupKeys:messKeys, schema:"single" }),
      fetchMetrics({ level:dataLevel, areaCodes:[areaCode],      theme:"invariant_h2h",      groupKeys:hhKeys,   schema:"single" }),
      fetchMetrics({ level:dataLevel, areaCodes:[areaCode],      theme:"invariant_industry", groupKeys:allIndKeys, schema:"single" }),
      // state benchmarks
      fetchMetrics({ level:"states", areaCodes:[resolvedFips],  theme:"invariant_message",  groupKeys:messKeys, schema:"single" }),
      fetchMetrics({ level:"states", areaCodes:[resolvedFips],  theme:"invariant_industry", groupKeys:allIndKeys, schema:"single" }),
      fetchMetrics({ level:"states", areaCodes:[resolvedFips],  theme:"invariant_h2h",      groupKeys:hhKeys,   schema:"single" }),
      // national benchmark
      fetchMetrics({ level:"national", areaCodes:[], theme:"invariant_message", groupKeys:messKeys, schema:"single" }),
      fetchMetrics({ level:"national", areaCodes:[], theme:"invariant_h2h",     groupKeys:hhKeys,   schema:"single" }),
      fetchMetrics({ level:"national", areaCodes:[], theme:"invariant_industry", groupKeys:allIndKeys, schema:"single" }),
      // fallback national for H2H (weighted or simple avg across states)
      fetchMetrics({ level:"states", areaCodes:Object.values(STATE_ABBR_TO_FIPS), theme:"invariant_h2h", groupKeys:hhKeys, schema:"single" }),
      // state-wide CD rows for map coloring
      fetch(`/api/${dataLevel}_data_arrow/`, {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({
          stateCodes: resolvedFips ? [resolvedFips] : [],
          theme: "invariant_message",
          groupKeys: messGroups.themes_convincing.keys,
          schema: "single",
          schema_type: "single",
        }),
      }).then(res => res.ok ? res.arrayBuffer() : Promise.reject(res)),
      // age splits
      fetchMetrics({ level:dataLevel, areaCodes:[areaCode], theme:"invariant_message", groupKeys:messKeys, schema:"single", filters:{ age:1 } }),
      fetchMetrics({ level:dataLevel, areaCodes:[areaCode], theme:"invariant_message", groupKeys:messKeys, schema:"single", filters:{ age:2 } }),
      fetchMetrics({ level:dataLevel, areaCodes:[areaCode], theme:"invariant_message", groupKeys:messKeys, schema:"single", filters:{ age:3 } }),
      // education splits
      fetchMetrics({ level:dataLevel, areaCodes:[areaCode], theme:"invariant_message", groupKeys:messKeys, schema:"single", filters:{ education:1 } }),
      fetchMetrics({ level:dataLevel, areaCodes:[areaCode], theme:"invariant_message", groupKeys:messKeys, schema:"single", filters:{ education:2 } }),
      // top issues — district + state (tracking schema, client-side row matching)
      fetchTopIssueRows(dataLevel, resolvedFips ? [resolvedFips] : [], 3),
      fetchTopIssueRows("states",  resolvedFips ? [resolvedFips] : [], 3),
      fetchTopIssueRows("states", [], 3),
    ])
    .then(([messRows, hhRows, indRows, stMessRows, stIndRows, stHhRows, natMessRows, natHhRows, natIndRows, natHhStateRows, mapBuf, age1Rows, age2Rows, age3Rows, edu1Rows, edu2Rows, topIssueRows, stTopIssueRows, natTopIssueRows]) => {
      if (!isActive) return;
      const messRow      = findRow(messRows);
      const hhRow        = findRow(hhRows);
      const indRow       = findRow(indRows);
      const stMess       = findStRow(stMessRows);
      const stInd        = findStRow(stIndRows);
      const stHh         = findStRow(stHhRows);
      const natMess      = (natMessRows || [])[0] || null;
      const natHhDirect  = (natHhRows || [])[0]   || null;
      const natHhFallback = computeWeightedAvgRow(natHhStateRows, hhKeys);
      const natHh        = natHhDirect || natHhFallback;
      const natInd       = (natIndRows || [])[0]  || null;
      const age1Row      = findRow(age1Rows);
      const age2Row      = findRow(age2Rows);
      const age3Row      = findRow(age3Rows);
      const edu1Row      = findRow(edu1Rows);
      const edu2Row      = findRow(edu2Rows);
      const topIssueRow    = findTopIssueRow(topIssueRows);
      const stTopIssueRow  = findTopIssueStateRow(stTopIssueRows);
      const natTopIssueRow = computeWeightedAvgRow(natTopIssueRows, topIssueKeys);
      if (typeof window !== "undefined") {
        console.log("[InvariantReport][TopIssues] areaType:", areaType, "areaCode:", areaCode, "stateFips:", resolvedFips);
        console.log("[InvariantReport][TopIssues] areaRow:", topIssueRow);
        console.log("[InvariantReport][TopIssues] stateRow:", stTopIssueRow);
        console.log("[InvariantReport][TopIssues] nationalRow:", natTopIssueRow);
        if (isStateReport) {
          console.log("[InvariantReport][H2H] stateRow:", stHh);
          console.log("[InvariantReport][H2H] nationalRow:", natHh);
        }
      }
      try {
        const t = tableFromIPC(mapBuf);
        setThemeRows(rowsFromTable(t));
      } catch { setThemeRows([]); }

      /* ── Theme ranking ── */
      const convGroup = messGroups.themes_convincing;
      const benchMess = isStateReport ? natMess : stMess;
      let convValues = convGroup.keys
        .map(k => ({
          key:k, label:convGroup.labels[k], color:convGroup.colors[k],
          value: Number(messRow?.[k] ?? 0),
          stValue: Number(benchMess?.[k] ?? 0),
          delta: benchMess ? Number(messRow?.[k]??0) - Number(benchMess?.[k]??0) : null,
          natValue: Number(natMess?.[k] ?? 0),
          natDelta: natMess ? Number(messRow?.[k]??0) - Number(natMess?.[k]??0) : null,
        }))
        .sort((a,b) => b.value - a.value);

      const convScale = convValues.length && convValues[0].value <= 1 ? 100 : 1;
      if (convScale !== 1) {
        convValues = convValues.map(v => ({
          ...v,
          value: v.value * convScale,
          stValue: v.stValue * convScale,
          delta: v.delta != null ? v.delta * convScale : null,
          natValue: v.natValue * convScale,
          natDelta: v.natDelta != null ? v.natDelta * convScale : null,
        }));
      }

      const topThemeKey   = convValues[0]?.key;
      const topThemeName  = convValues[0]?.label  ?? "";
      const topThemeColor = convValues[0]?.color   ?? PRO_COLOR;
      const bottomTheme   = convValues[convValues.length - 1];

      /* ── Effective reach (renamed: "overall impact score") ── */
      const topMessagesAcrossThemes = Object.entries(THEME_KEY_TO_ARG_GROUP)
        .flatMap(([themeKey, argGroupId]) => {
          const argGroup  = messGroups[argGroupId];
          if (!argGroup) return [];
          const themeColor  = convGroup.colors[themeKey] ?? "#999";
          const themePctRaw = Number(messRow?.[themeKey] ?? 0);
          return argGroup.keys.map(k => {
            const withinRaw   = Number(messRow?.[k] ?? 0);
            const isFrac      = themePctRaw <= 1 && withinRaw <= 1;
            const themePct    = isFrac ? themePctRaw * 100 : themePctRaw;
            const withinPct   = isFrac ? withinRaw * 100 : withinRaw;
            const effectiveReach = isFrac ? (themePctRaw * withinRaw * 100) : (themePctRaw * withinRaw) / 100;
            const themeLabel = convGroup.labels[themeKey] ?? argGroupId;
            return { k, val: effectiveReach, withinPct, themePct, label: argGroup.labels[k], color: argGroup.colors[k], themeLabel, themeColor };
          });
        })
        .filter(m => m.val > 0)
        .sort((a, b) => b.val - a.val)
        .slice(0, 5);

      /* ── H2H ranking ── */
      const hhEntries = Object.entries(hhGroups).map(([gid,g]) => {
        const proKey  = Array.isArray(g.polarity?.pos) ? g.polarity.pos[0] : g.polarity?.pos;
        const antiKey = Array.isArray(g.polarity?.neg) ? g.polarity.neg[0] : g.polarity?.neg;
        const proVal  = Number(hhRow?.[proKey]  ?? 0);
        const antiVal = Number(hhRow?.[antiKey] ?? 0);
        const benchHh = isStateReport ? natHh : stHh;
        const stPro   = Number(benchHh?.[proKey]   ?? 0);
        const stAnti  = Number(benchHh?.[antiKey]  ?? 0);
        const natPro  = Number(natHh?.[proKey]  ?? 0);
        const natAnti = Number(natHh?.[antiKey] ?? 0);
        const margin    = proVal - antiVal;
        const stMargin  = Number.isFinite(stPro) && Number.isFinite(stAnti) ? stPro - stAnti : null;
        const natMargin = Number.isFinite(natPro) && Number.isFinite(natAnti) ? natPro - natAnti : null;
        return {
          id:gid, shortLabel:g.label.replace(/^[^:]+:\s*/i,""),
          proLabel:proKey ? g.labels[proKey]:"", antiLabel:antiKey ? g.labels[antiKey]:"",
          proVal, antiVal, margin, stMargin, natMargin,
        };
      }).sort((a,b) => b.margin - a.margin);

      // H2H watchouts — frames where anti wins locally (margin < 0) or anti wins vs benchmark
      const hhWatchouts = hhEntries.filter(hh => {
        const benchMargin = isStateReport ? hh.natMargin : hh.stMargin;
        if (hh.margin < 0) return true;
        if (benchMargin != null && hh.margin - benchMargin < -3) return true;
        return false;
      }).slice(0, 2);

      /* ── Industry core ── */
      const benchInd = isStateReport ? natInd : stInd;
      const favPos   = Number(indRow?.["ind_favorability__pos_pred_n"]     ?? 0);
      const favNeg   = Number(indRow?.["ind_favorability__neg_pred_n"]     ?? 0);
      const favNeu   = Number(indRow?.["ind_favorability__neither_pred_n"] ?? 0);
      const legSup   = Number(indRow?.["leg_support__support_pred_n"]      ?? 0);
      const legOpp   = Number(indRow?.["leg_support__oppose_pred_n"]       ?? 0);
      const legNeu   = Number(indRow?.["leg_support__neither_pred_n"]      ?? 0);
      const stFavPos = Number(benchInd?.["ind_favorability__pos_pred_n"]      ?? 0);
      const stFavNeg = Number(benchInd?.["ind_favorability__neg_pred_n"]      ?? 0);
      const stLegSup = Number(benchInd?.["leg_support__support_pred_n"]       ?? 0);
      const stLegOpp = Number(benchInd?.["leg_support__oppose_pred_n"]        ?? 0);

      /* ── Trusted messengers — top 3 from information group ── */
      const infoGroup = indGroups.information;
      const messengerRanked = infoGroup.keys
        .map(k => ({ key:k, label:infoGroup.labels[k], value: Number(indRow?.[k] ?? 0) }))
        .sort((a,b) => b.value - a.value)
        .slice(0, 3);

      /* ── Awareness — which threats are known; overall awareness ── */
      const awarenessGroup = indGroups.awareness;
      const notHeardVal = Number(indRow?.["awareness__not_heard_pred_n"] ?? 50);
      const awarenessRanked = awarenessGroup.keys
        .filter(k => k !== "awareness__not_heard_pred_n")
        .map(k => ({ key:k, label:awarenessGroup.labels[k], value: Number(indRow?.[k] ?? 0) }))
        .sort((a,b) => b.value - a.value)
        .slice(0, 3);

      /* ── News channels ── */
      const newsGroup = indGroups.news_source;
      const stNewsRow = benchInd;
      const newsRanked = newsGroup.keys
        .map(k => {
          const rawVal = Number(indRow?.[k] ?? 0);
          const rawSt = Number(stNewsRow?.[k] ?? 0);
          const scale = rawVal <= 1 && rawSt <= 1 ? 100 : 1;
          const value = rawVal * scale;
          const stValue = rawSt * scale;
          return {
            key:k,
            label:newsGroup.labels[k],
            value,
            stValue,
            delta: Number.isFinite(stValue) ? value - stValue : null,
          };
        })
        .sort((a,b) => b.value - a.value)
        .slice(0, 3);

      /* ── Buying approach ── */
      const buyGroup = indGroups.buy_approach;
      const buyRanked = buyGroup.keys
        .map(k => {
          const rawVal = Number(indRow?.[k] ?? 0);
          const rawSt = Number(benchInd?.[k] ?? 0);
          const scale = rawVal <= 1 && rawSt <= 1 ? 100 : 1;
          const value = rawVal * scale;
          const stValue = rawSt * scale;
          return { key:k, label:buyGroup.labels[k], value, stValue, delta: (Number.isFinite(stValue) ? value - stValue : null) };
        })
        .sort((a,b) => b.value - a.value);
      const topBuy = buyRanked[0];

      /* ── Favorability change (persuasion proof) ── */
      // Try mess aggregator's industry_posneg_change group first
      const favChangeGroup = messGroups.industry_posneg_change;
      const favMoved = favChangeGroup
        ? Number(messRow?.["industry_change__pos_pred_n"] ?? indRow?.["industry_change__pos_pred_n"] ?? 0)
        : 0;
      const favChangeScale = favMoved <= 1 ? 100 : 1;
      const favMovedPct = favMoved * favChangeScale;

      /* ── Top message by age group (full theme coverage) ── */
      const pickTopTheme = (row) => {
        if (!row) return null;
        let best = null;
        convGroup.keys.forEach(k => {
          const v = Number(row?.[k] ?? 0) * (Number(row?.[k] ?? 0) <= 1 ? 100 : 1);
          if (!best || v > best.value) {
            best = { key:k, value:v, label:convGroup.labels[k], color:convGroup.colors[k] };
          }
        });
        return best;
      };
      // Pick the top argument within the top theme for a given row, using effective reach (theme% × within-arg%)
      const pickTopArgForTheme = (row, themeKey) => {
        if (!row || !themeKey) return null;
        const argGroupId = THEME_KEY_TO_ARG_GROUP[themeKey];
        const argGroup   = messGroups[argGroupId];
        if (!argGroup) return null;
        const themePctRaw = Number(row?.[themeKey] ?? 0);
        const isFrac      = themePctRaw <= 1;
        const themePct    = isFrac ? themePctRaw * 100 : themePctRaw;
        let best = null;
        argGroup.keys.forEach(k => {
          const withinRaw      = Number(row?.[k] ?? 0);
          const withinPct      = isFrac ? withinRaw * 100 : withinRaw;
          const effectiveReach = isFrac ? (themePctRaw * withinRaw * 100) : (themePct * withinPct) / 100;
          if (!best || effectiveReach > best.effectiveReach) {
            best = { key:k, effectiveReach, withinPct, themePct, label:argGroup.labels[k] };
          }
        });
        return best;
      };
      const topMessageByAge = [
        { label:"18–34", row:age1Row },
        { label:"35–64", row:age2Row },
        { label:"65+",   row:age3Row },
      ].map(({ label, row }) => {
        const best = pickTopTheme(row);
        const topArg = best ? pickTopArgForTheme(row, best.key) : null;
        return best
          ? { ageLabel:label, ...best, topArg }
          : { ageLabel:label, label:"—", value:null, color:"#94a3b8", topArg:null };
      });

      // Pick top argument across ALL themes for a row
      const pickTopArgAcrossThemes = (row) => {
        if (!row) return null;
        let best = null;
        Object.entries(THEME_KEY_TO_ARG_GROUP).forEach(([themeKey, argGroupId]) => {
          const argGroup = messGroups[argGroupId];
          if (!argGroup) return;
          const themePctRaw = Number(row?.[themeKey] ?? 0);
          const isFrac = themePctRaw <= 1;
          const themePct = isFrac ? themePctRaw * 100 : themePctRaw;
          const themeColor = convGroup.colors[themeKey] ?? "#999";
          const themeLabel = convGroup.labels[themeKey] ?? argGroupId;
          argGroup.keys.forEach(k => {
            const withinRaw = Number(row?.[k] ?? 0);
            const withinPct = isFrac ? withinRaw * 100 : withinRaw;
            const effectiveReach = isFrac ? (themePctRaw * withinRaw * 100) : (themePct * withinPct) / 100;
            if (!best || effectiveReach > best.effectiveReach) {
              best = { key:k, effectiveReach, withinPct, themePct, label:argGroup.labels[k], themeKey, themeColor, themeLabel };
            }
          });
        });
        return best;
      };

      // Build a map of overall effective reach per argument key for over-indexing comparison
      const overallReachByKey = {};
      Object.entries(THEME_KEY_TO_ARG_GROUP).forEach(([themeKey, argGroupId]) => {
        const argGroup = messGroups[argGroupId];
        if (!argGroup) return;
        const themePctRaw = Number(messRow?.[themeKey] ?? 0);
        const isFrac = themePctRaw <= 1;
        const themePct = isFrac ? themePctRaw * 100 : themePctRaw;
        argGroup.keys.forEach(k => {
          const withinRaw = Number(messRow?.[k] ?? 0);
          const effectiveReach = isFrac ? (themePctRaw * withinRaw * 100) : (themePct * (isFrac ? withinRaw * 100 : withinRaw)) / 100;
          overallReachByKey[k] = effectiveReach;
        });
      });

      // For each education group, find the argument that most over-indexes vs overall
      const pickMostOverIndexing = (row) => {
        if (!row) return null;
        let best = null;
        Object.entries(THEME_KEY_TO_ARG_GROUP).forEach(([themeKey, argGroupId]) => {
          const argGroup = messGroups[argGroupId];
          if (!argGroup) return;
          const themePctRaw = Number(row?.[themeKey] ?? 0);
          const isFrac = themePctRaw <= 1;
          const themePct = isFrac ? themePctRaw * 100 : themePctRaw;
          const themeColor = convGroup.colors[themeKey] ?? "#999";
          const themeLabel = convGroup.labels[themeKey] ?? argGroupId;
          argGroup.keys.forEach(k => {
            const withinRaw = Number(row?.[k] ?? 0);
            const withinPct = isFrac ? withinRaw * 100 : withinRaw;
            const effectiveReach = isFrac ? (themePctRaw * withinRaw * 100) : (themePct * withinPct) / 100;
            const overallReach = overallReachByKey[k] ?? 0;
            const delta = effectiveReach - overallReach;
            if (!best || delta > best.delta) {
              best = { key:k, effectiveReach, withinPct, themePct, label:argGroup.labels[k], themeKey, themeColor, themeLabel, delta, overallReach };
            }
          });
        });
        return best;
      };

      const topMessageByEdu = [
        { label:"No degree", row:edu1Row },
        { label:"Degree",    row:edu2Row },
      ].map(({ label, row }) => {
        const topArg = pickMostOverIndexing(row);
        const bestTheme = topArg
          ? { key:topArg.themeKey, color:topArg.themeColor, label:topArg.themeLabel }
          : pickTopTheme(row);
        return topArg
          ? { eduLabel:label, ...bestTheme, topArg }
          : { eduLabel:label, label:"—", value:null, color:"#94a3b8", topArg:null };
      });

      /* ── Top issues ranking ── */
      const topIssueGroup = polGroups.pol_top_issues;
      const benchIssueRow = isStateReport ? natTopIssueRow : stTopIssueRow;
      const topIssuesRanked = topIssueGroup
        ? topIssueGroup.keys.map(k => {
            const raw   = Number(topIssueRow?.[k]  ?? 0);
            const rawSt = Number(benchIssueRow?.[k] ?? 0);
            const scale = (raw <= 1 && rawSt <= 1) ? 100 : 1;
            return {
              key: k,
              label: topIssueGroup.labels[k] ?? k,
              value: raw * scale,
              stValue: rawSt * scale,
              delta: benchIssueRow ? (raw - rawSt) * scale : null,
            };
          })
            .sort((a, b) => {
              if (b.value !== a.value) return b.value - a.value;
              if (b.stValue !== a.stValue) return b.stValue - a.stValue;
              return String(a.label || "").localeCompare(String(b.label || ""));
            })
            .slice(0, 5)
        : [];

      setMessData({ convValues, topThemeKey, topThemeName, topThemeColor, bottomTheme, topMessagesAcrossThemes, topMessageByAge, topMessageByEdu });
      setHhData({ topHH:hhEntries.slice(0,3), bottomHH:hhEntries[hhEntries.length-1], hhWatchouts, allHH:hhEntries });
      setIndData({ favPos, favNeg, favNeu, legSup, legOpp, legNeu, stFavPos, stFavNeg, stLegSup, stLegOpp });
      setIndustryData({ messengerRanked, awarenessRanked, notHeardAwareness:notHeardVal, newsRanked, topBuy, buyRanked, favMovedPct, topMessengers:messengerRanked });
      setTopIssuesData(topIssuesRanked);
      if (typeof window !== "undefined") {
        console.log("[InvariantReport][BestTheme]", topThemeName, topThemeColor, convValues[0]);
      }
      setSurveyLoading(false);
    })
    .catch(err => { console.error("[InvariantReport]", err); if (isActive) setSurveyLoading(false); });

    return () => { isActive = false; };
  }, [areaCode, stateFips, areaType]);

  /* ── Derived ── */
  const topThemeName  = messData?.topThemeName  ?? "";
  const topThemeColor = messData?.topThemeColor ?? PRO_COLOR;
  const convValues    = messData?.convValues    ?? [];
  const maxConvVal    = convValues[0]?.value    || 100;
  const bestThemeVsNational = convValues
    .filter(v => Number.isFinite(v.natDelta))
    .sort((a,b) => (b.natDelta ?? -Infinity) - (a.natDelta ?? -Infinity))[0] || null;
  const bestH2HVsNational = (hhData?.topHH ?? [])
    .filter(h => Number.isFinite(h.natMargin))
    .sort((a,b) => ((b.margin - b.natMargin) ?? -Infinity) - ((a.margin - a.natMargin) ?? -Infinity))[0] || null;
  const reportLoading = surveyLoading || charts.loading || demoLoading;

  // Render-complete gate (headless PDF): the aggregate data flag AND the theme
  // map's GeoJSON (which loads in a sub-component AFTER surveyLoading settles, so
  // it is NOT part of reportLoading). onReady fires only once BOTH are ready.
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    if (!reportLoading && mapReady && typeof onReady === "function") onReady();
  }, [reportLoading, mapReady, onReady]);

  return (
    <Box
      id={id || "report-content"}
      sx={{
        backgroundColor:"white", color:"#1a202c",
        width:"794px", height:"1123px", overflow:"hidden",
        mx:"auto", p:"14px",
        border:"1px solid #cbd5e1",
        boxSizing:"border-box",
        display:"flex", flexDirection:"column",
        gap:"7px",
        position:"relative",
      }}
    >
      {reportLoading && (
        <Box
          sx={{
            position:"absolute",
            inset:0,
            bgcolor:"rgba(255,255,255,0.65)",
            backdropFilter:"blur(2px)",
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            zIndex:5,
          }}
        >
          <Box sx={{ display:"flex", alignItems:"center", gap:"10px", px:"12px", py:"8px", borderRadius:"999px", bgcolor:"white", border:"1px solid #e2e8f0", boxShadow:"0 6px 20px rgba(15,23,42,0.08)" }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize:"0.75rem", color:"#475569", fontWeight:600 }}>
              Building report…
            </Typography>
          </Box>
        </Box>
      )}
      <ClientTopHeader
        districtName={districtName} state={state} district={district}
        topVI={topVI} viRow={viRow} currentParty={demRow?.house_2024_party}
        stackLogo={stackLogo} pcpcLogo={pcpcLogo} invariantLogo={invariantLogo}
      />

      {/* ─── DEMOGRAPHICS ─── */}
      <Box sx={{ flexShrink:0 }}>
        {demoLoading ? (
          <SectionSkeleton height={60} lines={2} />
        ) : (
          <DistrictDemographicBlock
            demoRow={demRow}
            demoBenchmark={isStateReport ? US_NATIONAL : demoBench}
            districtName={districtName}
            stateName={isStateReport ? "U.S." : (stateName || state)}
            demoLoading={demoLoading}
            benchLabel={benchLabelLong}
            areaLabel={areaLabel}
          />
        )}
      </Box>

      {/* ─── ROWS A B C wrapper — fills remaining height ─── */}
      <Box sx={{ flex:"1 1 0", minHeight:0, display:"grid", gridTemplateRows:"auto 1fr auto", gap:"7px" }}>

      {/* ─── ROW A: MAP + THEME RANKING ─── */}
      <Box sx={{ display:"grid", gridTemplateColumns:"210px 1fr", gap:"8px", alignItems:"stretch" }}>

        {/* Map */}
        <Box sx={{ border:"1px solid #e2e8f0", borderRadius:"10px", overflow:"hidden", bgcolor:"#f8fafc", height:"100%" }}>
          {surveyLoading ? (
            <Skeleton variant="rectangular" height="100%" />
          ) : (
            <DistrictThemeMap
              level={areaType}
              stateFips={stateFips || STATE_ABBR_TO_FIPS[stateAbbr]}
              district={isCD ? district : areaCode}
              themeName={topThemeName}
              topThemeColor={topThemeColor}
              themeRows={themeRows}
              themeKeys={getThemeConfig().groupsById.themes_convincing.keys}
              themeColors={getThemeConfig().groupsById.themes_convincing.colors}
              onGeoReady={() => setMapReady(true)}
            />
          )}
        </Box>

        {/* Theme ranking */}
        <Box sx={{ display:"flex", flexDirection:"column", bgcolor:"white", border:"1px solid #e2e8f0", borderRadius:"10px", p:"10px", overflow:"hidden" }}>
          <SectionLabel>Best Message Theme</SectionLabel>
          {surveyLoading ? (
            <SectionSkeleton height={60} lines={3} />
          ) : messData && (
            <Box sx={{ background:`linear-gradient(135deg, ${alpha(topThemeColor,0.12)} 0%, ${alpha(topThemeColor,0.05)} 100%)`, border:`1.5px solid ${alpha(topThemeColor,0.35)}`, borderLeft:`5px solid ${topThemeColor}`, borderRadius:"8px", px:"10px", py:"7px", mb:"6px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <Box>
                <Typography sx={{ fontSize:"0.62rem", color:alpha(topThemeColor,0.8), textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700 }}>Top theme</Typography>
                <Typography sx={{ fontSize:"1.15rem", fontWeight:900, color:topThemeColor, lineHeight:1.1 }}>{topThemeName}</Typography>
              </Box>
              <Box sx={{ textAlign:"right" }}>
                <Typography sx={{ fontSize:"1.4rem", fontWeight:900, color:topThemeColor, lineHeight:1 }}>{fmt1(convValues[0]?.value)}</Typography>
                {convValues[0]?.delta != null && <DeltaChip delta={convValues[0].delta} label={benchLabel} />}
              </Box>
            </Box>
          )}
          {!surveyLoading && convValues.map((t, i) => (
            <Box key={t.key} sx={{ mb:"3px", display:"flex", alignItems:"center", gap:"6px" }}>
              <Box sx={{ width:6, height:6, borderRadius:"50%", bgcolor:t.color, flexShrink:0 }} />
              <Typography sx={{ fontSize:"0.70rem", color: i===0 ? "#0f172a" : "#475569", fontWeight: i===0 ? 700 : 400, lineHeight:1.2, flex:1, minWidth:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                {t.label}
              </Typography>
              <Box sx={{ width:"90px", height:5, borderRadius:999, bgcolor:"#e2e8f0", overflow:"hidden", flexShrink:0 }}>
                <Box sx={{ height:"100%", width:`${Math.min(100, Math.max(0, t.value))}%`, bgcolor:t.color, borderRadius:999, opacity: i===0 ? 1 : 0.55 }} />
              </Box>
              <Box sx={{ display:"flex", alignItems:"center", flexShrink:0, minWidth:"72px", justifyContent:"flex-end" }}>
                <Typography sx={{ fontSize:"0.70rem", fontWeight:700, color:"#0f172a" }}>{fmt1(t.value)}</Typography>
                {Number.isFinite(t.delta) && <DeltaChip delta={t.delta} label={benchLabel} />}
              </Box>
            </Box>
          ))}
          {/* Age-split theme read */}
          {!surveyLoading && messData?.topMessageByAge && (
            <Box sx={{ mt:"5px", display:"flex", gap:"3px" }}>
              {messData.topMessageByAge.map(a => (
                <Box key={a.ageLabel} sx={{ flex:1, px:"5px", py:"3px", borderRadius:"5px", bgcolor:alpha(a.color,0.08), border:`1px solid ${alpha(a.color,0.25)}` }}>
                  <Typography sx={{ fontSize:"0.54rem", fontWeight:800, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.07em" }}>{a.ageLabel}</Typography>
                  <Typography sx={{ fontSize:"0.62rem", fontWeight:700, color:a.color, lineHeight:1.2 }}>{a.label}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* ─── ROW B: (TOP ARGUMENTS + PERSUASION PROOF) left | H2H right ─── */}
      <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1.5fr", gap:"8px", alignItems:"stretch", minHeight:"280px", overflow:"hidden" }}>

        {/* Left col: Top Arguments stacked above Persuasion Proof */}
        <Box sx={{ display:"flex", flexDirection:"column", gap:"3px", overflow:"hidden", minHeight:0, height:"100%" }}>

          {/* Top Arguments */}
          <Box sx={{ border:"1px solid #e2e8f0", borderRadius:"10px", p:"8px", bgcolor:"white", flexShrink:0 }}>
          
              <Box sx={{ display:"inline-block", cursor:"help", mb:"5px" }}>
                <SectionLabel color="#2D6A9F">Top Arguments </SectionLabel>
              </Box>

            {surveyLoading ? (
              <SectionSkeleton height={50} lines={2} />
            ) : (messData?.topMessagesAcrossThemes ?? []).map((m, i) => (
              <Box key={m.k} sx={{ mb:"3px", display:"flex", alignItems:"center", gap:"6px", px:"7px", py:"4px", borderRadius:"7px", border:`1px solid ${i===0 ? alpha(m.themeColor,0.4) : alpha(m.themeColor,0.2)}`, bgcolor: i===0 ? alpha(m.themeColor,0.07) : alpha(m.themeColor,0.03) }}>
                <Typography sx={{ fontSize:"0.6rem", fontWeight:900, color: i===0 ? "white" : m.themeColor, width:16, height:16, borderRadius:"50%", bgcolor: i===0 ? m.themeColor : alpha(m.themeColor,0.15), display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{i+1}</Typography>
                <Box sx={{ flex:1, minWidth:0 }}>
                  <Typography sx={{ fontSize:"0.74rem", color:"#0f172a", fontWeight: i===0 ? 700 : 600, lineHeight:1.2 }}>{m.label}</Typography>
                  <Typography sx={{ fontSize:"0.54rem", color:m.themeColor, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em" }}>{m.themeLabel}</Typography>
                </Box>
                <Typography sx={{ fontSize:"0.80rem", fontWeight:900, color:m.themeColor, lineHeight:1, flexShrink:0 }}>{m.val.toFixed(1)}%</Typography>
              </Box>
            ))}
          </Box>

          {/* Audience Focus — over-indexing by education */}
          {surveyLoading ? (
            <SectionSkeleton height={50} lines={2} />
          ) : (() => {
            const rawDist   = Number(demRow?.education_degreeplus ?? 0);
            const benchDemo = isStateReport ? US_NATIONAL : demoBench;
            const rawSt     = Number(benchDemo?.education_degreeplus ?? 0);
            const distCollege = rawDist > 0 && rawDist <= 1 ? rawDist * 100 : rawDist;
            const stCollege   = rawSt   > 0 && rawSt   <= 1 ? rawSt   * 100 : rawSt;
            const collegeDelta = (distCollege > 0 && stCollege > 0) ? distCollege - stCollege : 0;
            const hasCollegeSignal = Math.abs(collegeDelta) > 2;

            const eduSigs = messData?.topMessageByEdu ?? [];
            const degreeEntry   = eduSigs.find(e => e.eduLabel === "Degree");
            const noDegreeEntry = eduSigs.find(e => e.eduLabel === "No degree");

            const groups = hasCollegeSignal
              ? (collegeDelta > 0
                ? [degreeEntry, noDegreeEntry]   // lead with degree since more college
                : [noDegreeEntry, degreeEntry])   // lead with no-degree since fewer college
              : [degreeEntry, noDegreeEntry];

            const isDiverging = degreeEntry?.topArg?.label !== noDegreeEntry?.topArg?.label;

            return (
              <Box sx={{ border:`1.5px solid ${alpha(PRO_COLOR,0.2)}`, borderLeft:`4px solid ${PRO_COLOR}`, borderRadius:"10px", p:"8px", bgcolor:"white", flex:1 }}>
                <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:"6px" }}>
                  <Box sx={{ display:"flex", alignItems:"center", gap:"4px" }}>
                    <PeopleIcon sx={{ fontSize:13, color:PRO_COLOR }} />
                    <SectionLabel color={PRO_COLOR}>Audience Focus</SectionLabel>
                  </Box>
                  <Box sx={{ display:"flex", gap:"3px" }}>
                    {hasCollegeSignal && (
                      <Box sx={{ px:"6px", py:"2px", borderRadius:"20px", bgcolor:alpha(PRO_COLOR,0.08), border:`1px solid ${alpha(PRO_COLOR,0.2)}` }}>
                        <Typography sx={{ fontSize:"0.57rem", fontWeight:800, color:PRO_COLOR }}>
                          {Math.abs(collegeDelta).toFixed(0)}pp {collegeDelta > 0 ? "more" : "fewer"} degree holders
                        </Typography>
                      </Box>
                    )}
            
                  </Box>
                </Box>
                <Box sx={{ display:"flex", flexDirection:"column", gap:"4px" }}>
                  {groups.filter(Boolean).map((entry) => {
                    if (!entry?.topArg) return null;
                    const tc = entry.topArg.themeColor || PRO_COLOR;
                    const delta = entry.topArg.delta ?? 0;
                    const isPos = delta >= 0;
                    return (
                      <Box key={entry.eduLabel} sx={{ p:"5px 7px", borderRadius:"7px", bgcolor:alpha(tc,0.05), border:`1px solid ${alpha(tc,0.2)}` }}>
                        <Box sx={{ display:"flex", alignItems:"center", gap:"4px", mb:"2px" }}>
                          <Box sx={{ width:6, height:6, borderRadius:"50%", bgcolor:tc, flexShrink:0 }} />
                          <Typography sx={{ fontSize:"0.57rem", fontWeight:800, color:tc, textTransform:"uppercase", letterSpacing:"0.07em", flex:1 }}>
                            {entry.eduLabel}
                          </Typography>
                          <Box sx={{ display:"inline-flex", bgcolor: isPos ? "#dcfce7" : "#fee2e2", px:"4px", py:"1px", borderRadius:"3px", flexShrink:0 }}>
                            <Typography sx={{ fontSize:"0.57rem", fontWeight:800, color: isPos ? "#15803d" : "#b91c1c" }}>
                              {isPos ? "+" : ""}{delta.toFixed(1)}pp
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize:"0.72rem", fontWeight:700, color:"#0f172a", lineHeight:1.25 }}>
                          "{entry.topArg.label}"
                        </Typography>
                        <Typography sx={{ fontSize:"0.55rem", color:"#94a3b8", mt:"1px" }}>
                          {entry.topArg.themeLabel} · {entry.topArg.effectiveReach?.toFixed(1)}% reach
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })()}

        </Box>

        {/* Right col: H2H — full height */}
        <Box sx={{ border:"1px solid #e2e8f0", borderRadius:"10px", p:"8px", bgcolor:"white", display:"flex", flexDirection:"column", boxShadow:"0 1px 3px rgba(0,0,0,0.06)", height:"100%" }}>
          <SectionLabel color={ANTI_COLOR}>Best Head-to-Head Frames</SectionLabel>
          <Box sx={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-start", gap:"8px" }}>
            <Box>
              {surveyLoading && <SectionSkeleton height={60} lines={2} />}
              {(hhData?.topHH ?? []).slice(0,3).map((hh, i) => (
                <Box key={hh.id} sx={{ mb:"4px", p:"6px 8px", borderRadius:"8px", border:`1px solid ${i===0 ? alpha(PRO_COLOR,0.4) : alpha(PRO_COLOR,0.15)}`, bgcolor: i===0 ? alpha(PRO_COLOR,0.07) : "#fafcff", boxShadow: i===0 ? `0 1px 4px ${alpha(PRO_COLOR,0.12)}` : "none" }}>
                  <Typography sx={{ fontSize:"0.52rem", fontWeight:800, color:"#94a3b8", letterSpacing:"0.1em", textTransform:"uppercase", mb:"2px" }}>
                    {hh.shortLabel}
                  </Typography>
                  {(() => {
                    const proWins = hh.margin >= 0;
                    const winnerLabel = proWins ? hh.proLabel : hh.antiLabel;
                    const winnerColor = proWins ? PRO_COLOR : ANTI_COLOR;
                    return (
                      <Box sx={{ display:"flex", alignItems:"baseline", gap:"4px", mb:"4px" }}>
                        <Typography sx={{ fontSize:"0.58rem", color:winnerColor, fontWeight:800 }}>✓</Typography>
                        <Typography sx={{ fontSize:"0.72rem", color:winnerColor, fontWeight:700, lineHeight:1.2 }}>{winnerLabel}</Typography>
                      </Box>
                    );
                  })()}
                  <Box sx={{ display:"flex", alignItems:"center", gap:"3px", flexWrap:"wrap" }}>
                    <Box sx={{ px:"5px", py:"1px", borderRadius:"20px", bgcolor:alpha(PRO_COLOR,0.12), border:`1px solid ${alpha(PRO_COLOR,0.3)}` }}>
                      <Typography sx={{ fontSize:"0.56rem", fontWeight:800, color:PRO_COLOR }}>
                        Margin {fmtPP(hh.margin)}
                      </Typography>
                    </Box>
                    {(() => {
                      const benchMargin = isStateReport ? hh.natMargin : hh.stMargin;
                      return Number.isFinite(benchMargin) ? (
                        <Box sx={{ px:"4px", py:"1px", borderRadius:"20px", bgcolor:"#f1f5f9", border:"1px solid #e2e8f0" }}>
                          <Typography sx={{ fontSize:"0.56rem", fontWeight:700, color:"#475569" }}>
                            vs {benchLabel} {fmtPP(hh.margin - benchMargin)}
                          </Typography>
                        </Box>
                      ) : null;
                    })()}
                    <Typography sx={{ fontSize:"0.54rem", fontWeight:700, color:"#64748b", ml:"2px" }}>
                      Pro {fmt1(hh.proVal)} · Anti {fmt1(hh.antiVal)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            {/* H2H Watchout — only shown when PCPC is actually losing a frame */}
            {!surveyLoading && (() => {
              // allHH contains every H2H pair sorted by margin, including losers
              const allHH = hhData?.allHH ?? [];
              // Only surface frames where PCPC is genuinely losing (negative margin)
              const losingFrames = allHH.filter(hh => hh.margin < 0);
              if (losingFrames.length === 0) return null;
              // Show the two worst losses (most negative margins)
              const worstProList = [...losingFrames].sort((a, b) => a.margin - b.margin).slice(0, 2);
              return (
                <Box sx={{ mt:"4px", p:"6px 8px", borderRadius:"8px", bgcolor:alpha(ANTI_COLOR,0.05), border:`1.5px solid ${alpha(ANTI_COLOR,0.25)}`, borderLeft:`4px solid ${ANTI_COLOR}` }}>
                  <Box sx={{ display:"flex", alignItems:"center", gap:"4px", mb:"2px" }}>
                    <WarningAmberIcon sx={{ fontSize:11, color:ANTI_COLOR }} />
                    <Typography sx={{ fontSize:"0.57rem", fontWeight:800, color:ANTI_COLOR, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                      Don't Lead With
                    </Typography>
                  </Box>
                  {worstProList.map((worstPro) => {
                    const benchMargin = isStateReport ? worstPro.natMargin : worstPro.stMargin;
                    const worseVsBench = Number.isFinite(benchMargin) && (worstPro.margin - benchMargin) < -2;
                    return (
                      <Box key={worstPro.id} sx={{ mb:"3px" }}>
                        <Typography sx={{ fontSize:"0.64rem", color:"#0f172a", fontWeight:700, lineHeight:1.3, mb:"1px" }}>
                          "{worstPro.proLabel}"
                        </Typography>
                        <Typography sx={{ fontSize:"0.58rem", color:"#475569", lineHeight:1.3 }}>
                          Opposition leads by {fmtPP(Math.abs(worstPro.margin))} here
                          {worseVsBench ? ` (${fmtPP(worstPro.margin - benchMargin)} vs ${benchLabel})` : ""}.{" "}
                          <Box component="span" sx={{ fontWeight:700, color:"#64748b" }}>
                            Pro {fmt1(worstPro.proVal)} · Anti {fmt1(worstPro.antiVal)}
                          </Box>
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </Box>
        </Box>

      </Box>

      {/* ─── ROW C: TRUSTED MESSENGERS + AWARENESS + NEWS ─── */}
      <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"8px", minHeight:"190px", maxHeight:"220px" }}>

        {/* Trusted Messengers */}
        <Box sx={{ border:"1px solid #e2e8f0", borderRadius:"10px", p:"10px", bgcolor:"white", overflow:"hidden" }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:"4px", mb:"4px" }}>
            <PeopleIcon sx={{ fontSize:13, color:"#2D6A9F" }} />
            <SectionLabel color="#2D6A9F">Trusted Messengers</SectionLabel>
          </Box>
          <Typography sx={{ fontSize:"0.66rem", color:"#94a3b8", mb:"6px", lineHeight:1.3 }}>
            Most credible to speak on personal care products
          </Typography>
          {surveyLoading ? (
            <SectionSkeleton height={45} lines={2} />
          ) : (industryData?.messengerRanked ?? []).map((m, i) => (
            <Box key={m.key} sx={{ mb:"3px", display:"flex", alignItems:"center", gap:"6px", px:"7px", py:"5px", borderRadius:"7px", bgcolor: i===0 ? alpha("#2D6A9F",0.07) : "#f8fafc", border:`1px solid ${i===0 ? alpha("#2D6A9F",0.25) : "#f1f5f9"}` }}>
              <Box sx={{ width:18, height:18, borderRadius:"50%", bgcolor: i===0 ? "#2D6A9F" : "#e2e8f0", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <Typography sx={{ fontSize:"0.55rem", fontWeight:900, color: i===0 ? "white" : "#64748b" }}>#{i+1}</Typography>
              </Box>
              <Typography sx={{ fontSize:"0.73rem", fontWeight: i===0 ? 700 : 500, color:"#0f172a", flex:1, minWidth:0, lineHeight:1.2 }}>{m.label}</Typography>
              <Typography sx={{ fontSize:"0.73rem", fontWeight:800, color:"#2D6A9F", flexShrink:0 }}>{fmt1(m.value)}</Typography>
            </Box>
          ))}
          {(() => {
            const researchItem = (industryData?.buyRanked ?? []).find((b) => b.key?.toLowerCase().includes("research"));
            const isHigher = Number.isFinite(researchItem?.delta) ? researchItem.delta > 0 : false;
            return researchItem && isHigher;
          })() && (
            <Box sx={{ mt:"5px", p:"4px 7px", borderRadius:"6px", bgcolor:alpha(NAVY,0.05), border:`1px solid ${alpha(NAVY,0.12)}` }}>
              <Typography sx={{ fontSize:"0.6rem", fontWeight:800, color:NAVY, mb:"1px" }}>⚑ High research audience</Typography>
              <Typography sx={{ fontSize:"0.59rem", color:"#64748b", lineHeight:1.3 }}>Lead with evidence and expert citations.</Typography>
            </Box>
          )}
        </Box>

        {/* Top Issues */}
        <Box sx={{ border:"1px solid #e2e8f0", borderRadius:"10px", p:"10px", bgcolor:"white", overflow:"hidden" }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:"4px", mb:"4px" }}>
            <VerifiedUserIcon sx={{ fontSize:13, color:AMBER }} />
            <SectionLabel color={AMBER}>Top Issues</SectionLabel>
          </Box>
          <Typography sx={{ fontSize:"0.66rem", color:"#94a3b8", mb:"6px", lineHeight:1.3 }}>
            What constituents say matters most (vs. {isStateReport ? "national" : "state"})
          </Typography>
          {surveyLoading && <SectionSkeleton height={50} lines={2} />}
          {!surveyLoading && topIssuesData.map((issue, i) => (
            <Box key={issue.key} sx={{ mb:"3px", display:"flex", alignItems:"center", gap:"5px", px:"7px", py:"4px", borderRadius:"6px", bgcolor: i===0 ? alpha(AMBER,0.07) : "#f8fafc", border:`1px solid ${i===0 ? alpha(AMBER,0.22) : "#f1f5f9"}` }}>
              <Typography sx={{ fontSize:"0.58rem", fontWeight:900, color: i===0 ? AMBER : "#94a3b8", width:12, flexShrink:0 }}>{i+1}</Typography>
              <Typography sx={{ fontSize:"0.70rem", fontWeight: i===0 ? 700 : 400, color:"#0f172a", flex:1, minWidth:0, lineHeight:1.2 }}>{issue.label}</Typography>
              <Typography sx={{ fontSize:"0.70rem", fontWeight:800, color: i===0 ? AMBER : "#475569", flexShrink:0 }}>{issue.value.toFixed(1)}%</Typography>
              {Number.isFinite(issue.delta) && (
                Math.abs(issue.delta) > 0.25 ? (
                  <Box sx={{ display:"inline-flex", bgcolor: issue.delta > 0 ? "#dcfce7" : "#fee2e2", px:"3px", py:"1px", borderRadius:"3px", flexShrink:0 }}>
                    <Typography sx={{ fontSize:"0.55rem", fontWeight:800, color: issue.delta > 0 ? "#15803d" : "#b91c1c" }}>
                      {`${issue.delta > 0 ? "+" : ""}${issue.delta.toFixed(1)}pp`}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display:"inline-flex", bgcolor:"#f1f5f9", px:"3px", py:"1px", borderRadius:"3px", flexShrink:0 }}>
                    <Typography sx={{ fontSize:"0.55rem", fontWeight:800, color:"#94a3b8" }}>≈</Typography>
                  </Box>
                )
              )}
            </Box>
          ))}
        </Box>

        {/* News & Channel */}
        <Box sx={{ border:"1px solid #e2e8f0", borderRadius:"10px", p:"10px", bgcolor:"white", overflow:"hidden" }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:"4px", mb:"4px" }}>
            <TvIcon sx={{ fontSize:13, color:NAVY }} />
            <SectionLabel color={NAVY}>Where to Reach Them</SectionLabel>
          </Box>
          <Typography sx={{ fontSize:"0.66rem", color:"#94a3b8", mb:"6px", lineHeight:1.3 }}>
            Main source of news in the area
          </Typography>
          {surveyLoading ? (
            <SectionSkeleton height={55} lines={2} />
          ) : (industryData?.newsRanked ?? []).map((n, i) => (
            <Box key={n.key} sx={{ mb:"4px" }}>
              <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:"2px" }}>
                <Typography sx={{ fontSize:"0.71rem", color:"#0f172a", fontWeight: i===0 ? 700 : 400 }}>{n.label}</Typography>
                <Box sx={{ display:"flex", alignItems:"center", gap:"3px", flexShrink:0 }}>
                  <Typography sx={{ fontSize:"0.71rem", fontWeight:700, color:NAVY }}>{fmt1(n.value)}</Typography>
                  {!isStateReport && Number.isFinite(n.delta) && Math.abs(n.delta) >= 1 && (
                    <Box sx={{ px:"4px", py:"1px", borderRadius:"20px", bgcolor: n.delta > 0 ? "#dcfce7" : "#fee2e2" }}>
                      <Typography sx={{ fontSize:"0.56rem", fontWeight:800, color: n.delta > 0 ? "#15803d" : "#b91c1c" }}>{fmtPP(n.delta)}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ height:5, borderRadius:999, bgcolor:"#f1f5f9", overflow:"hidden" }}>
                <Box sx={{ height:"100%", width:`${Math.min(100, Math.max(0, n.value))}%`, bgcolor: i===0 ? NAVY : alpha(NAVY,0.35), borderRadius:999 }} />
              </Box>
            </Box>
          ))}
        </Box>

      </Box>

      </Box>{/* end rows wrapper */}

    </Box>
  );
};

export default InvariantDistrictReportView;
